import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { syncFromStrava, getStravaAuthUrl, checkStravaConnection } from '../../services/systemService';
import COLORS from '../../utils/colors';
import { FaStrava, FaSync, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { getFrontendUrl } from '../../utils/config';

const SystemSettings: React.FC = () => {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [stravaConnecting, setStravaConnecting] = useState(false);
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaAthleteName, setStravaAthleteName] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Sửa hàm useEffect để xử lý state từ location
  useEffect(() => {
    // Kiểm tra nếu có state từ location (chuyển hướng từ StravaCallback)
    if (location.state) {
      if (location.state.stravaConnected) {
        // Cập nhật trạng thái kết nối thành công
        setStravaConnected(true);
        setStravaAthleteName(location.state.athleteName || null);
        setSuccess(true);
        setSuccessMessage('Kết nối Strava thành công!');
        setError(null);
      } else if (location.state.stravaError) {
        // Hiển thị lỗi từ quá trình kết nối
        setError(location.state.errorMessage || 'Có lỗi xảy ra khi kết nối với Strava');
        setSuccess(false);
      }
      
      // Xóa state để tránh hiển thị lại thông báo khi refresh
      navigate(location.pathname, { replace: true });
    }
    
    // Lưu ý: Chúng ta không cần xử lý code từ URL nữa vì đã được xử lý ở component StravaCallback
    // Component StravaCallback đã gọi API exchange-token và chuyển hướng về đây với state
  }, [location, navigate]);
  
  // Thêm useEffect mới để kiểm tra kết nối Strava khi component mount
  useEffect(() => {
    // Kiểm tra kết nối Strava từ localStorage trước tiên
    const localStravaConnected = localStorage.getItem('stravaConnected') === 'true';
    const localStravaAthleteName = localStorage.getItem('stravaAthleteName');
    
    console.log('Thông tin Strava từ localStorage:', { localStravaConnected, localStravaAthleteName });
    
    if (localStravaConnected) {
      setStravaConnected(true);
      if (localStravaAthleteName) {
        setStravaAthleteName(localStravaAthleteName);
      }
    }
    
    // Sau đó vẫn kiểm tra API để cập nhật thông tin mới nhất
    const checkExistingStravaConnection = async () => {
      try {
        console.log('Đang kiểm tra kết nối Strava từ API...');
        const isConnected = await checkStravaConnection();
        console.log('Kết quả kiểm tra kết nối Strava từ API:', isConnected);
        
        if (isConnected) {
          console.log('API xác nhận đã kết nối Strava');
          setStravaConnected(true);
          
          // Cập nhật lại localStorage
          localStorage.setItem('stravaConnected', 'true');
          
          // Lưu ý: Không có thông tin về athleteName nữa từ API mới
          // Tiếp tục sử dụng thông tin athleteName từ localStorage nếu có
        } else {
          console.log('API xác nhận chưa kết nối Strava');
          // Nếu API trả về chưa kết nối, ta vẫn ưu tiên giữ thông tin từ localStorage
          if (!localStravaConnected) {
            setStravaConnected(false);
            localStorage.removeItem('stravaConnected');
            localStorage.removeItem('stravaAthleteName');
          }
        }
      } catch (error) {
        console.error('Lỗi khi kiểm tra kết nối Strava từ API:', error);
        // Nếu API lỗi, vẫn giữ thông tin từ localStorage
      }
    };

    checkExistingStravaConnection();
  }, []);
  
  // Hàm đồng bộ dữ liệu nhân viên từ Strava
  const handleSyncEmployees = async () => {
    try {
      // Kiểm tra đã kết nối Strava chưa
      if (!stravaConnected) {
        setError('Bạn cần kết nối với Strava trước khi đồng bộ dữ liệu nhân viên');
        return;
      }
      
      setSyncing(true);
      setSuccess(false);
      setError(null);
      
      console.log('Bắt đầu đồng bộ nhân viên từ Strava...');
      const result = await syncFromStrava();
      console.log('Kết quả đồng bộ nhân viên:', result);
      
      setSyncing(false);
      setSuccess(true);
      setSuccessMessage('Đồng bộ dữ liệu nhân viên thành công!');
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Lỗi khi đồng bộ nhân viên:', error);
      setSyncing(false);
      setError(error instanceof Error ? error.message : 'Đã xảy ra lỗi khi đồng bộ dữ liệu nhân viên');
    }
  };
  
  const handleConnectStrava = () => {
    const frontendUrl = getFrontendUrl();
    window.location.href = `https://www.strava.com/oauth/authorize?client_id=126234&response_type=code&redirect_uri=${frontendUrl}/exchange_token&approval_prompt=force&scope=activity:read_all`;
  };

  return (
    <div style={{ 
      padding: '20px',
      backgroundColor: COLORS.background.main,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`
    }}>
      <h2 style={{ color: COLORS.text.light, fontSize: '24px', marginBottom: '20px' }}>
        Cài Đặt Hệ Thống
      </h2>
      
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: COLORS.text.light, fontSize: '18px', marginBottom: '15px' }}>
          <FaStrava style={{ marginRight: '8px' }} />
          Kết nối với Strava
        </h3>
        
        {stravaConnected ? (
          <div style={{ 
            backgroundColor: 'rgba(76,175,80,0.1)', 
            padding: '15px', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <FaCheck style={{ color: COLORS.status.success, marginRight: '10px' }} />
            <div>
              <p style={{ color: COLORS.text.light, margin: '0 0 5px 0' }}>
                Đã kết nối với Strava
              </p>
              {stravaAthleteName && (
                <p style={{ color: COLORS.text.medium, margin: 0, fontSize: '14px' }}>
                  Người dùng: {stravaAthleteName}
                </p>
              )}
            </div>
          </div>
        ) : (
          <button 
            onClick={handleConnectStrava}
            disabled={stravaConnecting}
            style={{
              backgroundColor: stravaConnecting ? COLORS.status.disabled : '#FC5200',
              color: COLORS.text.light,
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: stravaConnecting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaStrava size={20} />
            {stravaConnecting ? 'Đang kết nối...' : 'Kết nối với Strava'}
            {stravaConnecting && (
              <div style={{ 
                width: '16px', 
                height: '16px', 
                border: `2px solid ${COLORS.text.light}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
          </button>
        )}
      </div>
      
      <div style={{ color: COLORS.text.medium }}>
        <h3 style={{ color: COLORS.text.light, fontSize: '18px', marginBottom: '15px' }}>
          <FaSync style={{ marginRight: '8px' }} />
          Đồng bộ dữ liệu
        </h3>
        
        <button 
          onClick={handleSyncEmployees}
          disabled={syncing || !stravaConnected}
          style={{
            backgroundColor: syncing || !stravaConnected ? 'rgba(252, 82, 0, 0.3)' : '#FC5200',
            color: syncing || !stravaConnected ? 'rgba(255, 255, 255, 0.6)' : COLORS.text.light,
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: syncing || !stravaConnected ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            opacity: syncing || !stravaConnected ? 0.7 : 1,
            boxShadow: syncing || !stravaConnected ? 'none' : '0 2px 4px rgba(252, 82, 0, 0.2)'
          }}
        >
          {syncing ? 'Đang đồng bộ Nhân Viên...' : 'Đồng bộ Nhân Viên Từ Strava'}
          {syncing && (
            <div style={{ 
              width: '16px', 
              height: '16px', 
              border: `2px solid ${COLORS.text.light}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </button>
        
        {!stravaConnected && (
          <p style={{ 
            color: COLORS.status.warning, 
            marginTop: '10px', 
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <FaExclamationTriangle />
            Bạn cần kết nối với Strava trước khi đồng bộ dữ liệu
          </p>
        )}
        
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        
        {error && (
          <div style={{ 
            color: COLORS.status.error, 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: 'rgba(255,0,0,0.1)', 
            borderRadius: '4px' 
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{ 
            color: COLORS.status.success, 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: 'rgba(76,175,80,0.1)', 
            borderRadius: '4px' 
          }}>
            {successMessage || 'Thao tác thành công!'}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettings; 